import React, { Fragment, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useAuth } from "../../contexts/auth.context";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import * as yup from "yup";
import { Alert } from "@mui/material";
import { VTextLink } from "../common/VTextLink";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useMobile } from "../../hooks/useMobile";

export type LoginInputs = {
    email: string;
    password: string;
};
const Login: React.FC = () => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const { login, isAuthenticated, loading } = useAuth();
    const { isTablet, isMobile } = useMobile();
    const { push } = useAppNavigate();
    useEffect(() => {
        if (!loading && isAuthenticated) {
            if (!isTablet && isMobile) {
                push("/detect-mobile");
            } else {
                push("/home");
            }
        }
    }, [loading, isAuthenticated, push, isTablet, isMobile]);
    const onSubmit = async (values: LoginInputs) => {
        const { email, password } = values;
        try {
            await login(email, password);
            setError(undefined);
        } catch (err) {
            setError(err as string);
        }
    };
    return (
        <Fragment>
            <Container maxWidth={"sm"}>
                <Box mb={4}>
                    <Typography variant="h2" component={"h1"} color="textPrimary" align="left">
                        Sign Back In
                    </Typography>
                </Box>
                <Formik
                    initialValues={{
                        email: "",
                        password: "",
                    }}
                    onSubmit={onSubmit}
                    validationSchema={yup.object({
                        email: yup.string().email("A valid email is required").required("Email is required").max(100),
                        password: yup.string().required("Password is required"),
                    })}
                    validateOnChange={false}
                >
                    {({ submitForm, errors, values, handleChange, touched, isSubmitting }) => {
                        return (
                            <Form>
                                <FormControl variant="outlined" fullWidth margin={"normal"}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        variant="outlined"
                                        value={values.email}
                                        onChange={handleChange}
                                        helperText={errors.email && touched.email && errors.email}
                                        error={errors.email ? true : false}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" fullWidth margin={"normal"}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleChange}
                                        id="password"
                                        label="Password"
                                        value={values.password}
                                        type={showPassword ? "text" : "password"}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        onMouseDown={() => setShowPassword(!showPassword)}
                                                        size="large"
                                                    >
                                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        autoComplete="on"
                                        helperText={errors.password && touched.password && errors.password}
                                        error={errors.password ? true : false}
                                    />
                                </FormControl>
                                {error && <Alert severity={"error"}>{error}</Alert>}
                                <Typography color="textSecondary" align="right" variant="body2">
                                    <VTextLink to="/forgot">Forgotten Password</VTextLink>
                                </Typography>
                                <Box mt={2}>
                                    <Button
                                        type="submit"
                                        size="large"
                                        color="secondary"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        onClick={submitForm}
                                        fullWidth
                                    >
                                        {!loading ? "Sign In" : <CircularProgress />}
                                    </Button>
                                </Box>
                                <Box mt={2}>
                                    <Button
                                        size="large"
                                        color="primary"
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => push("/organisation/signup")}
                                    >
                                        Create an Account
                                    </Button>
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </Container>
        </Fragment>
    );
};
export default Login;
